import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSubscriptionBannerState } from "../../hooks/useSubscriptionBannerState";
import { BrandLoader } from "../ui/BrandLoader";

/**
 * Route guard for the upgrade-pitch surfaces (`/brochure/:moduleSlug`).
 *
 * WHY A ROUTE GUARD AND NOT A LINK GUARD: the brochures were reachable by URL by
 * anyone — no role check, no tier check, not in any registry that could switch
 * them off. Hiding the links would have left the pages exactly as reachable as
 * they were; the ROUTE has to refuse, so the refusal lives in the route element.
 *
 * The admission condition is `SubscriptionBanner`'s own — a non-null
 * `useSubscriptionBannerState().state`, i.e. Free, Trial (any), post-trial, or
 * Paid inside the 30-day renewal window. A healthy long-term Paid tenant, and an
 * Operator, are not an upgrade audience and get bounced. See that hook for the
 * one deliberate difference from the band (`isRestricted` is a banner-precedence
 * rule, not an audience rule).
 *
 * Three states, never two — same discipline as `RoleProtectedRoute`: unresolved
 * is NOT a denial, because `coerceTier(undefined)` is "Free" and deciding early
 * decides wrong in both directions.
 */

// Hard ceiling on waiting for /Subscription/me. Only reachable if the request
// neither resolves nor rejects (a hung fetch); without it the guard would spin
// forever. Timing out DENIES — a sales brochure is the one thing that should
// fail closed rather than open.
const MAX_RESOLVE_WAIT_MS = 15000;

interface SubscriptionAudienceRouteProps {
  children: React.ReactElement;
}

export const SubscriptionAudienceRoute = ({ children }: SubscriptionAudienceRouteProps) => {
  const { state, isResolved } = useSubscriptionBannerState();
  const [waitTimedOut, setWaitTimedOut] = useState(false);

  useEffect(() => {
    if (isResolved) return;
    const timer = setTimeout(() => setWaitTimedOut(true), MAX_RESOLVE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isResolved]);

  if (state) return children;

  if (!isResolved && !waitTimedOut) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <BrandLoader variant="inline" size="md" />
      </div>
    );
  }

  /*
    Not an upgrade audience. Bounce silently to the workspace rather than
    explaining a page they were never meant to see.
    "/" and not "/dashboard": there IS no `/dashboard` route in App.tsx (grep it
    — the only matches are module-scoped dashboards), so redirecting there would
    land on the catch-all instead of the home page.
  */
  return <Navigate to="/" replace />;
};

export default SubscriptionAudienceRoute;
